/**
 * Pure edge-routing primitives shared between the live board renderer
 * and the Types-view interface preview.
 *
 * Everything in this module operates on geometric primitives (rects,
 * anchors, signed offsets) and emits SVG path strings. There is no
 * React, no DOM, no model-aware code — the live renderer wraps it with
 * planning (side choice, slot allocation, mutual-pair detection) and
 * the preview pipes synthetic anchors directly through `edgePath` so
 * the two paths render with byte-identical math.
 */

export type Side = "L" | "R" | "T" | "B";
export type Rect = { x: number; y: number; w: number; h: number };
export type Anchor = { x: number; y: number; dx: number; dy: number };

/** Compute the anchor sitting on a rect's side at a 0..1 fractional
 *  position along that side. `dx`/`dy` is the outward normal. */
export function anchorOnSide(r: Rect, s: Side, frac: number): Anchor {
  switch (s) {
    case "L":
      return { x: r.x, y: r.y + r.h * frac, dx: -1, dy: 0 };
    case "R":
      return { x: r.x + r.w, y: r.y + r.h * frac, dx: 1, dy: 0 };
    case "T":
      return { x: r.x + r.w * frac, y: r.y, dx: 0, dy: -1 };
    case "B":
      return { x: r.x + r.w * frac, y: r.y + r.h, dx: 0, dy: 1 };
  }
}

export const isHorizontal = (a: Anchor) => a.dx !== 0;

/** Tangent-axis gap below this triggers orthogonal routing instead of
 *  smooth bezier — keeps mutual-pair offsets visible as parallel
 *  segments rather than two tangled bends. */
export const ORTHO_SWITCH_MAX = 40;
export const CORNER_RADIUS = 10;

/** Mutual-pair (two-way) rail gap: the two legs of an A↔B pair render as
 *  two parallel offsets of ONE center path, held at this constant
 *  perpendicular distance along the whole run — train rails, not two
 *  independently-routed curves whose spacing drifts with the slope. */
export const RAIL_GAP = 10;

/** Perpendicular "neck" inset between the anchor on the wall and the
 *  point where the spline starts/ends. Sized to cover the arrowhead
 *  marker (markerWidth=8, refX=9 in a 0..10 viewBox → marker extends
 *  ~7.2 user units back from the path endpoint), so the curved body
 *  cannot bend inside the head no matter how tight the spline. The
 *  marker's tangent at the endpoint is along ±anchor-normal, which is
 *  exactly perpendicular to the wall — the head always reads straight
 *  out of the node face. */
export const NECK_LEN = 10;

/** Straight run required under an arrowhead so the line never enters the
 *  head's side: marker extends ~7.2 user units back from the endpoint
 *  (markerWidth=8, refX=9 in a 0..10 viewBox), rounded up. */
export const ARROW_CLEARANCE = 8;

// =============================================================================
// Wide-arc routes (metro style)
// =============================================================================
//
// Comfortable diagonal links render as axis-aligned runs joined by ONE wide
// quarter-circle corner (or two, for a facing Z) instead of a free spline —
// the same visual language as the orthogonal trunks, just with a radius
// scaled to the available legs. Arcs are emitted as kappa cubics so the
// flatten/midpoint/rail machinery consumes them unchanged. Tight gaps, wrap-
// arounds and busy hub sides (3+ edges, which fan better as splines) keep
// their existing routes.

/** Quarter circle as a cubic bezier: control handles at kappa × radius. */
const ARC_KAPPA = 0.5522847498;
/** Below this radius a wide arc reads no better than the small-corner or
 *  spline route it would replace — fall back instead. */
const ARC_MIN_RADIUS = 10;
/** A side with 3+ edges keeps splines: sibling arcs would stack onto the
 *  same elbow lane, splines fan apart naturally. Degree 2 → hubReach 1.15,
 *  degree 3 → 1.3 (see planner hub bundling). */
const ARC_HUB_REACH_MAX = 1.3 - 1e-6;

/** `L` into the arc start + kappa-cubic quarter arc around `elbow`, turning
 *  from travel direction d1 into travel direction d2. */
function arcCorner(
  ex: number,
  ey: number,
  d1x: number,
  d1y: number,
  d2x: number,
  d2y: number,
  r: number,
): string {
  const sx = ex - d1x * r;
  const sy = ey - d1y * r;
  const px = ex + d2x * r;
  const py = ey + d2y * r;
  const k = ARC_KAPPA * r;
  return (
    `L ${sx} ${sy} ` +
    `C ${sx + d1x * k} ${sy + d1y * k}, ${px - d2x * k} ${py - d2y * k}, ${px} ${py}`
  );
}

/** Arc route for ANGLE links — anchors on perpendicular walls (mixed
 *  tangents) become an axis-aligned L with one circular corner. The radius
 *  is one continuum: it grows toward a wide quarter-circle when the legs
 *  are long and shrinks to a small turn in tight spots (`rFloor` 3 — the
 *  tight-elbow regime), so the shape never pops while a node is dragged
 *  across a routing threshold. The terminal leg always keeps a straight
 *  ARROW_CLEARANCE run under the head. Parallel-wall pairs return null and
 *  keep their spline: a Z of arcs reads stiffer than the smooth S. */
export function arcRoutePath(
  f: Anchor,
  t: Anchor,
  offset: number,
  rFloor: number = ARC_MIN_RADIUS,
): string | null {
  void offset; // slot spread is expressed via anchor positions, like bezier
  const dx = t.x - f.x;
  const dy = t.y - f.y;
  const fH = isHorizontal(f);
  if (fH === isHorizontal(t)) return null;

  // L-shape with the elbow on f's axis.
  const elbowOutwardOfF = fH ? dx * f.dx > 0 : dy * f.dy > 0;
  const elbowOutwardOfT = fH ? dy * t.dy < 0 : dx * t.dx < 0;
  if (!elbowOutwardOfF || !elbowOutwardOfT) return null;
  const legF = fH ? Math.abs(dx) : Math.abs(dy);
  const legT = fH ? Math.abs(dy) : Math.abs(dx);
  const r = Math.min(legF - 2, legT - ARROW_CLEARANCE, 0.85 * Math.min(legF, legT));
  if (r < rFloor) return null;
  const ex = fH ? t.x : f.x;
  const ey = fH ? f.y : t.y;
  const d1x = fH ? Math.sign(dx) : 0;
  const d1y = fH ? 0 : Math.sign(dy);
  const d2x = fH ? 0 : Math.sign(dx);
  const d2y = fH ? Math.sign(dy) : 0;
  return `M ${f.x} ${f.y} ${arcCorner(ex, ey, d1x, d1y, d2x, d2y, r)} L ${t.x} ${t.y}`;
}

/** Smooth bezier — used for comfortable gaps along the tangent axis.
 *  `offset` is reserved for symmetry with orthogonalPath; bezier
 *  expresses spatial offset by shifting the anchor positions
 *  themselves (mutual pairs, sibling fans), not by tilting tangents.
 *
 *  Path shape: `anchor → neck → spline → neck → anchor`. The two short
 *  L segments at the ends are along each anchor's outward normal, so
 *  the spline only ever lives between the two neck points and a tight
 *  curve cannot bleed into the arrowhead. */
export function bezierPath(f: Anchor, t: Anchor, offset: number, hubReach: number): string {
  const dx = t.x - f.x;
  const dy = t.y - f.y;
  const dist = Math.hypot(dx, dy) || 1;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  // Outward room at each end: projection of the chord onto the anchor's
  // outward normal. Positive and small = a tight facing gap, where neck and
  // reach must shrink with the space or the curve folds into an S between
  // the walls. Non-positive = wrap-around, where the space beyond the wall
  // is free and full-size neck/reach are what make the loop readable.
  const fOut = f.dx * dx + f.dy * dy;
  const tOut = -(t.dx * dx + t.dy * dy);
  const antiparallel = f.dx === -t.dx && f.dy === -t.dy;
  // Sideways offset between the anchors of an antiparallel pair. Small
  // lateral + small gap = the anchors truly face each other across a narrow
  // corridor; large lateral = the run has room and the gap is only nominal.
  const lateral = isHorizontal(f) ? absDy : absDx;

  // Aligned anchors across a tiny facing gap: a straight segment is the
  // only shape that fits — any spline would fold inside the arrowhead.
  if (antiparallel && fOut > 0 && fOut <= 2 * ARROW_CLEARANCE && lateral <= 2) {
    return `M ${f.x} ${f.y} L ${t.x} ${t.y}`;
  }

  // Source neck scales with the gap (no arrowhead to protect). The target
  // neck never drops below ARROW_CLEARANCE: the head must sit entirely on a
  // straight run perpendicular to the wall, so in tight spots the stub pokes
  // past the nominal gap and the curve swings around to meet it — only a
  // true narrow corridor (target wall facing the source wall up close)
  // clamps it, since there the stub would cross into the source node.
  const fNeck = fOut <= 0 ? NECK_LEN : Math.min(NECK_LEN, Math.max(3, fOut * 0.3));
  let tNeck =
    tOut <= 0 ? NECK_LEN : Math.min(NECK_LEN, Math.max(ARROW_CLEARANCE, tOut * 0.3));
  if (
    antiparallel &&
    tOut > 0 &&
    tOut < 4 * ARROW_CLEARANCE &&
    lateral < 3 * ARROW_CLEARANCE
  ) {
    // Tight facing corridor: the head spans most of the gap no matter what,
    // so a long stub just reads as a straight poke — give the space back to
    // the curve. Arrival stays perpendicular via the end tangent (the last
    // control point sits on the anchor normal). 0.25·gap meets the free
    // formula's ARROW_CLEARANCE exactly at the 4·clearance bound, so the
    // stub length is continuous across the corridor edge.
    tNeck = Math.min(NECK_LEN, Math.max(1.5, tOut * 0.25));
  }

  // Free room between the two neck points of a facing pair — the space the
  // spline itself may use along the facing axis.
  const span = tOut - fNeck - tNeck;
  const reach = (axisDist: number, out: number, neck: number) => {
    // Minimum floor keeps the "outstanding" part of the curve visible
    // even when the tangent-axis gap is small, so edges never flatten
    // into a straight diagonal. Cap allows a bit of overshoot — better
    // to see a small S than a dead line.
    const base = Math.max(18, axisDist * 0.4) * hubReach;
    let r = Math.min(base, axisDist * 0.6 + 18);
    // Facing pair: both control tangents share the span between the necks,
    // keeping the control points ordered along the facing axis so the cubic
    // cannot double back. Other shapes cap per-end on their own outward room.
    if (antiparallel && fOut > 0 && tOut > 0) r = Math.min(r, Math.max(2, span / 2));
    else if (out > 0) r = Math.min(r, Math.max(2, out / 2 - neck));
    return r;
  };
  const fk = reach(isHorizontal(f) ? absDx : absDy, fOut, fNeck);
  const tk = reach(isHorizontal(t) ? absDx : absDy, tOut, tNeck);

  // Spline endpoints sit at the neck — up to NECK_LEN out along each
  // anchor's outward normal — not at the anchor itself.
  const fNx = f.x + f.dx * fNeck;
  const fNy = f.y + f.dy * fNeck;
  const tNx = t.x + t.dx * tNeck;
  const tNy = t.y + t.dy * tNeck;

  // Control points stay strictly on each neck's outward ray — the
  // tangent at each neck is purely perpendicular to its side, so the
  // spline leaves the neck at a clean 90° and only bends after
  // departing. The straight anchor↔neck tail then carries that 90°
  // tangent all the way to the wall.
  const c1x = fNx + f.dx * fk;
  const c1y = fNy + f.dy * fk;
  const c2x = tNx + t.dx * tk;
  const c2y = tNy + t.dy * tk;
  void offset;
  void dist;
  return [
    `M ${f.x} ${f.y}`,
    `L ${fNx} ${fNy}`,
    `C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tNx} ${tNy}`,
    `L ${t.x} ${t.y}`,
  ].join(" ");
}

/** Orthogonal route with rounded corners.
 *
 *  Trunk routes keep the long axis-aligned segment midway between the two
 *  anchor walls; the sibling-spread `offset` is clamped so both stems stay
 *  at least corner-radius + ARROW_CLEARANCE long. When the axis gap is too
 *  tight to hold even a minimal trunk — or an elbow's terminal leg would be
 *  shorter than the arrowhead (the line would enter the head's side) — the
 *  route falls back to the spline, which handles tight and negative
 *  positioning gracefully. */
export function orthogonalPath(f: Anchor, t: Anchor, offset: number): string {
  const dx = t.x - f.x;
  const dy = t.y - f.y;
  const dxSign = Math.sign(dx) || 1;
  const dySign = Math.sign(dy) || 1;
  const r = CORNER_RADIUS;

  if (isHorizontal(f) && isHorizontal(t)) {
    const trunkOutwardOfF = f.dx * dx > 0;
    const trunkOutwardOfT = t.dx * dx < 0;
    if (!trunkOutwardOfF || !trunkOutwardOfT) {
      return bezierPath(f, t, offset, 1);
    }
    if (Math.abs(dy) < 2 * r + 2) {
      return bezierPath(f, t, offset, 1);
    }
    const axis = Math.abs(dx);
    const rr = Math.max(3, Math.min(r, Math.abs(dy) / 2 - 1, axis / 2 - ARROW_CLEARANCE));
    const maxOff = axis / 2 - rr - ARROW_CLEARANCE;
    if (maxOff < 0) return bezierPath(f, t, offset, 1);
    const off = Math.max(-maxOff, Math.min(maxOff, offset * dxSign));
    const midX = (f.x + t.x) / 2 + off;
    return [
      `M ${f.x} ${f.y}`,
      `L ${midX - dxSign * rr} ${f.y}`,
      `Q ${midX} ${f.y} ${midX} ${f.y + dySign * rr}`,
      `L ${midX} ${t.y - dySign * rr}`,
      `Q ${midX} ${t.y} ${midX + dxSign * rr} ${t.y}`,
      `L ${t.x} ${t.y}`,
    ].join(" ");
  }
  if (!isHorizontal(f) && !isHorizontal(t)) {
    const trunkOutwardOfF = f.dy * dy > 0;
    const trunkOutwardOfT = t.dy * dy < 0;
    if (!trunkOutwardOfF || !trunkOutwardOfT) {
      return bezierPath(f, t, offset, 1);
    }
    if (Math.abs(dx) < 2 * r + 2) {
      // Straight only when the anchors are truly aligned — any visible slant
      // tilts the docked arrowhead off the wall normal; the spline docks it
      // perpendicular instead.
      if (Math.abs(dx) <= 2) {
        return `M ${f.x} ${f.y} L ${t.x} ${t.y}`;
      }
      return bezierPath(f, t, offset, 1);
    }
    const axis = Math.abs(dy);
    const rr = Math.max(3, Math.min(r, Math.abs(dx) / 2 - 1, axis / 2 - ARROW_CLEARANCE));
    const maxOff = axis / 2 - rr - ARROW_CLEARANCE;
    if (maxOff < 0) return bezierPath(f, t, offset, 1);
    const off = Math.max(-maxOff, Math.min(maxOff, offset * dySign));
    const midY = (f.y + t.y) / 2 + off;
    return [
      `M ${f.x} ${f.y}`,
      `L ${f.x} ${midY - dySign * rr}`,
      `Q ${f.x} ${midY} ${f.x + dxSign * rr} ${midY}`,
      `L ${t.x - dxSign * rr} ${midY}`,
      `Q ${t.x} ${midY} ${t.x} ${midY + dySign * rr}`,
      `L ${t.x} ${t.y}`,
    ].join(" ");
  }
  // Mixed tangents → the arc L (small-turn edge of the same continuum the
  // wide comfortable arcs live on); spline when even a 3px turn can't fit.
  return arcRoutePath(f, t, offset, 3) ?? bezierPath(f, t, offset, 1);
}

// =============================================================================
// On-path midpoint
// =============================================================================
//
// Edge badges/icons (the protocol chip, step number, decoration) must sit on
// the drawn line's middle. The chord midpoint — `(from + to) / 2` — only lands
// on the line for a straight diagonal; for a bezier it floats off to the side,
// and for an axis-aligned orthogonal route it sits in the empty corner the
// trunk routes AROUND. So we measure the geometric midpoint of the actual path
// by arc length, sampling the same `d` string the stroke renders. Pure + DOM-
// free (no getPointAtLength), so headless renders agree with the live board.

interface Pt {
  x: number;
  y: number;
}

const SAMPLES = 24; // per curved segment — enough for a stable arc-length mid

function cubicAt(p0: number, p1: number, p2: number, p3: number, u: number): number {
  const m = 1 - u;
  return m * m * m * p0 + 3 * m * m * u * p1 + 3 * m * u * u * p2 + u * u * u * p3;
}
function quadAt(p0: number, p1: number, p2: number, u: number): number {
  const m = 1 - u;
  return m * m * p0 + 2 * m * u * p1 + u * u * p2;
}

/** Flatten an SVG path (the M/L/C/Q subset our routers emit) into a polyline. */
export function flattenPathPoints(d: string): Pt[] {
  const pts: Pt[] = [];
  let cur: Pt = { x: 0, y: 0 };
  for (const m of d.matchAll(/([MLCQ])([^MLCQ]*)/g)) {
    const cmd = m[1]!;
    const n = (m[2]!.match(/-?\d*\.?\d+(?:e-?\d+)?/g) ?? []).map(Number);
    switch (cmd) {
      case "M":
        cur = { x: n[0]!, y: n[1]! };
        pts.push(cur);
        break;
      case "L":
        cur = { x: n[0]!, y: n[1]! };
        pts.push(cur);
        break;
      case "C": {
        const [x1, y1, x2, y2, x3, y3] = n;
        for (let i = 1; i <= SAMPLES; i++) {
          const u = i / SAMPLES;
          pts.push({
            x: cubicAt(cur.x, x1!, x2!, x3!, u),
            y: cubicAt(cur.y, y1!, y2!, y3!, u),
          });
        }
        cur = { x: x3!, y: y3! };
        break;
      }
      case "Q": {
        const [x1, y1, x2, y2] = n;
        for (let i = 1; i <= SAMPLES; i++) {
          const u = i / SAMPLES;
          pts.push({
            x: quadAt(cur.x, x1!, x2!, u),
            y: quadAt(cur.y, y1!, y2!, u),
          });
        }
        cur = { x: x2!, y: y2! };
        break;
      }
    }
  }
  return pts;
}

/** The point at half the total arc length of `d` — the true middle of the
 *  drawn line, whatever its shape. Falls back to the first point (or origin)
 *  for a degenerate path. */
export function pathMidpoint(d: string): Pt {
  const pts = flattenPathPoints(d);
  if (pts.length === 0) return { x: 0, y: 0 };
  if (pts.length === 1) return pts[0]!;
  let total = 0;
  const segLen: number[] = [];
  for (let i = 1; i < pts.length; i++) {
    const len = Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y);
    segLen.push(len);
    total += len;
  }
  if (total === 0) return pts[0]!;
  const half = total / 2;
  let acc = 0;
  for (let i = 0; i < segLen.length; i++) {
    const len = segLen[i]!;
    if (acc + len >= half) {
      const t = len === 0 ? 0 : (half - acc) / len;
      const a = pts[i]!;
      const b = pts[i + 1]!;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    acc += len;
  }
  return pts[pts.length - 1]!;
}

// =============================================================================
// Mutual-pair rails
// =============================================================================
//
// A mutual A↔B pair is ONE route: a single center path with a single anchor
// point per endpoint, drawn as two parallel lines offset ±RAIL_GAP/2
// perpendicular to the local tangent — so the gap stays constant through
// bends and corners, like train rails. Each leg keeps its own path element
// (own stroke colour, own arrowhead) and traverses the center in its own
// direction: the arrowheads enter/exit on either side of the shared anchor.

const RAIL_EPS = 1e-6;
/** Miter limit: at very sharp joins the miter point runs away; clamping the
 *  half-angle cosine to 1/4 caps the join at 4× the offset. Our routers only
 *  emit rounded corners, so this is a backstop, not a shaping decision. */
const RAIL_MITER_MIN_COS = 0.25;

const fmt = (v: number) => Math.round(v * 100) / 100;

/** Offset a polyline by `off` to the right of its travel direction (screen
 *  coords, y down). Miter joins hold the perpendicular distance to the
 *  source line constant through turns. */
function offsetPolyline(pts: readonly Pt[], off: number): Pt[] {
  // Segment unit normals (right of travel).
  const nx: number[] = [];
  const ny: number[] = [];
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i]!.x - pts[i - 1]!.x;
    const dy = pts[i]!.y - pts[i - 1]!.y;
    const len = Math.hypot(dx, dy) || 1;
    nx.push(-dy / len);
    ny.push(dx / len);
  }
  const out: Pt[] = [];
  for (let i = 0; i < pts.length; i++) {
    const prev = Math.max(0, i - 1);
    const next = Math.min(nx.length - 1, i);
    let mx = nx[prev]! + nx[next]!;
    let my = ny[prev]! + ny[next]!;
    const mlen = Math.hypot(mx, my);
    if (mlen < RAIL_EPS) {
      // 180° reversal — no bisector; fall back to the outgoing normal.
      mx = nx[next]!;
      my = ny[next]!;
    } else {
      mx /= mlen;
      my /= mlen;
    }
    const cos = Math.max(RAIL_MITER_MIN_COS, mx * nx[next]! + my * ny[next]!);
    out.push({ x: pts[i]!.x + (mx * off) / cos, y: pts[i]!.y + (my * off) / cos });
  }
  // Cusp collapse: where the source bends tighter than the offset radius,
  // the offset polyline folds into a switchback spike — right where an
  // arrowhead sits. Iteratively drop interior points that form a near-180°
  // turn until the polyline travels forward everywhere; endpoints stay (they
  // are the offset anchor points the heads dock to).
  let changed = true;
  while (changed && out.length > 2) {
    changed = false;
    for (let i = out.length - 2; i >= 1; i--) {
      const a = out[i - 1]!;
      const b = out[i]!;
      const c = out[i + 1]!;
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const bcx = c.x - b.x;
      const bcy = c.y - b.y;
      const al = Math.hypot(abx, aby);
      const bl = Math.hypot(bcx, bcy);
      if (al < RAIL_EPS || bl < RAIL_EPS) continue;
      if ((abx * bcx + aby * bcy) / (al * bl) < -0.3) {
        out.splice(i, 1);
        changed = true;
      }
    }
  }
  return out;
}

function polylineD(pts: readonly Pt[]): string {
  let d = `M ${fmt(pts[0]!.x)} ${fmt(pts[0]!.y)}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${fmt(pts[i]!.x)} ${fmt(pts[i]!.y)}`;
  return d;
}

/** Build both rails from one center path. `forward` runs the center's own
 *  direction on its travel-right side; `reverse` is the opposite rail
 *  emitted end→start, so its arrowhead (markerEnd) lands at the center's
 *  START wall — and, because reversal flips travel-right, both legs drive on
 *  the same-handed side of the shared center. */
export function railPaths(centerD: string, gap: number): { forward: string; reverse: string } {
  const raw = flattenPathPoints(centerD);
  const pts: Pt[] = [];
  for (const p of raw) {
    const last = pts[pts.length - 1];
    if (!last || Math.abs(p.x - last.x) > RAIL_EPS || Math.abs(p.y - last.y) > RAIL_EPS) {
      pts.push(p);
    }
  }
  if (pts.length < 2) return { forward: centerD, reverse: centerD };
  const forward = offsetPolyline(pts, gap / 2);
  const reverse = offsetPolyline(pts, -gap / 2).reverse();
  return { forward: polylineD(forward), reverse: polylineD(reverse) };
}

/** Pick a routing style: orthogonal when the tangent-axis gap is tight,
 *  wide-arc axis-aligned when there's room and the side isn't a busy hub,
 *  smooth bezier otherwise (wrap-arounds, near-aligned pairs, hub fans). */
export function edgePath(f: Anchor, t: Anchor, offset = 0, hubReach = 1): string {
  const fTangentAxisDist = isHorizontal(f) ? Math.abs(t.x - f.x) : Math.abs(t.y - f.y);
  const tTangentAxisDist = isHorizontal(t) ? Math.abs(t.x - f.x) : Math.abs(t.y - f.y);
  const minTangentGap = Math.min(fTangentAxisDist, tTangentAxisDist);
  if (minTangentGap < ORTHO_SWITCH_MAX) {
    return orthogonalPath(f, t, offset);
  }
  if (hubReach < ARC_HUB_REACH_MAX) {
    const arc = arcRoutePath(f, t, offset);
    if (arc) return arc;
  }
  return bezierPath(f, t, offset, hubReach);
}
